import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Facility, FacilityCreateRequest } from '@cortex/domain';

import { useAuth } from '@/auth';
import { cortexApi } from '@/services/cortexApi';
import { resolveApiError } from '@/services/errorMessages';
import { useToast } from '@/components/ui/Toast';
import { AdminMasterDetail } from './AdminMasterDetail';
import { FacilityList } from './FacilityList';
import { FacilityDetail } from './FacilityDetail';

/**
 * Controller for the Facilities tab of the Admin Module.
 *
 * Server state lives in TanStack Query under `['admin', 'facilities']`.
 * Mutations (create / activate) invalidate that key on success.
 *
 * Access model:
 *   - `facility.write`    → can create + activate.
 *   - `facility.read.all` → can list + view (read-only).
 *   Users lacking both get a 403 from the API and will never see this tab.
 *
 * Facilities are immutable once created. Clicking a row opens `view` mode
 * (read-only). "+ New facility" opens `create` mode.  There is no edit mode.
 */

const FACILITIES_KEY = ['admin', 'facilities'] as const;
const ENTITIES_KEY = ['admin', 'entities'] as const;

type DetailMode = 'idle' | 'view' | 'create';

export function FacilitiesTab() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  const canWrite = hasPermission('facility.write');

  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const [mode, setMode] = useState<DetailMode>('idle');
  const [formError, setFormError] = useState<string | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const facilitiesQuery = useQuery({
    queryKey: FACILITIES_KEY,
    queryFn: () => cortexApi.facilities.list(),
  });

  // We need brokerage entities for the create form's entity picker.
  const entitiesQuery = useQuery({
    queryKey: ENTITIES_KEY,
    queryFn: () => cortexApi.entities.list(),
    enabled: canWrite,
  });

  const facilities = useMemo<readonly Facility[]>(
    () => facilitiesQuery.data?.data ?? [],
    [facilitiesQuery.data],
  );

  const entities = useMemo(() => entitiesQuery.data?.entities ?? [], [entitiesQuery.data]);

  const listError = facilitiesQuery.isError
    ? formatError(resolveApiError(facilitiesQuery.error))
    : null;

  const selectedFacility = useMemo<Facility | null>(
    () => facilities.find((f) => f.id === selectedFacilityId) ?? null,
    [facilities, selectedFacilityId],
  );

  const createMutation = useMutation({
    mutationFn: (input: FacilityCreateRequest) => cortexApi.facilities.create(input),
    onSuccess: async (response) => {
      setFormError(null);
      showToast(
        `Facility v${response.data.version} created for ${response.data.entityName}.`,
        'success',
      );
      await queryClient.invalidateQueries({ queryKey: FACILITIES_KEY });
      setSelectedFacilityId(response.data.id);
      setMode('view');
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      setFormError(formatError(resolved));
      showToast(formatError(resolved), resolved.tone);
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => cortexApi.facilities.activate(id),
    onSuccess: async (response) => {
      showToast(
        `Facility v${response.data.version} for ${response.data.entityName} is now active.`,
        'success',
      );
      await queryClient.invalidateQueries({ queryKey: FACILITIES_KEY });
      // Re-select the activated row so the view panel refreshes.
      setSelectedFacilityId(response.data.id);
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      showToast(formatError(resolved), resolved.tone);
    },
  });

  const handleSelect = useCallback((id: string): void => {
    setSelectedFacilityId(id);
    setMode('view');
    setFormError(null);
    setMobileShowDetail(true);
  }, []);

  const handleCreateNew = useCallback((): void => {
    setSelectedFacilityId(null);
    setMode('create');
    setFormError(null);
    setMobileShowDetail(true);
  }, []);

  const handleCancel = useCallback((): void => {
    setMode('idle');
    setSelectedFacilityId(null);
    setFormError(null);
    setMobileShowDetail(false);
  }, []);

  const handleMobileBack = useCallback((): void => setMobileShowDetail(false), []);

  const handleCreate = useCallback(
    async (input: FacilityCreateRequest): Promise<void> => {
      setFormError(null);
      await createMutation.mutateAsync(input).catch(() => undefined);
    },
    [createMutation],
  );

  const handleActivate = useCallback(
    async (id: string): Promise<void> => {
      await activateMutation.mutateAsync(id).catch(() => undefined);
    },
    [activateMutation],
  );

  return (
    <AdminMasterDetail
      title="Facility Agreements"
      subtitle="Pre-agreed terms governing what each brokerage can quote and bind."
      mobileShowDetail={mobileShowDetail}
      onMobileBack={handleMobileBack}
      list={
        <FacilityList
          facilities={facilities}
          selectedFacilityId={selectedFacilityId}
          isLoading={facilitiesQuery.isLoading}
          errorMessage={listError}
          canCreate={canWrite}
          onSelect={handleSelect}
          onCreateNew={handleCreateNew}
        />
      }
      detail={
        <FacilityDetail
          mode={mode}
          facility={selectedFacility}
          entities={entities}
          isSaving={createMutation.isPending}
          isActivating={activateMutation.isPending}
          canWrite={canWrite}
          errorMessage={formError}
          onCreate={handleCreate}
          onActivate={handleActivate}
          onCancel={handleCancel}
        />
      }
    />
  );
}

const formatError = (r: { readonly title: string; readonly message: string }): string =>
  `${r.title}: ${r.message}`;
